import { ROUTES } from "@/types/routes";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import {
  useIsHomeDisplayReady,
  useRedEnvelopeRainList,
  OPEN_RED_ENVELOPE_RAIN_EVENT,
} from "../../../modules/homeDisplay/hooks";
import type { RedEnvelopeRainActivityVO } from "../../../modules/homeDisplay/types/homeDisplay.types";
import { loadConfig } from "../../../modules/homeDisplay/services";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { useT } from "../../../i18n/hooks";
import { useCurrencySymbol } from "../../../hooks/useUtils";
// 直接具体路径，不走 Base barrel
import { Message } from "../../Base/Message";
// 直接引触发器文件而不是组件 barrel：barrel 会把整个登录注册弹窗拖进本模块的依赖图
import { openLoginRegisterPopup } from "../../LoginRegisterPopup/triggerLoginRegisterPopup";
import type { RedEnvelopeRainStatus } from "../../../api/redEnvelopeRain";
// 轻量纯函数 / 请求模块直接按文件引用（弹窗组件本体 lazy，不进浮标所在 chunk 的静态闭包）
import {
  computeClockOffset,
  computeRainPhase,
  getNextRainBoundary,
  normalizeClaimCount,
  normalizeDailyTimes,
  normalizeRainSlots,
  serverNow,
} from "../components/RedEnvelopeRain/schedule";
import {
  fetchRainStatus,
  getRainUserId,
  type RainStatusSnapshot,
} from "../components/RedEnvelopeRain/statusCache";
import { getRainReasonText } from "../components/RedEnvelopeRain/reasonText";
import {
  formatRainAmount,
  getRainCurrencyId,
} from "../components/RedEnvelopeRain/money";
import type {
  RedEnvelopeRainFloatState,
  RedEnvelopeRainPopupResult,
} from "./useRedEnvelopeRainPopup.types";

export type {
  RedEnvelopeRainFloatState,
  RedEnvelopeRainPopupResult,
} from "./useRedEnvelopeRainPopup.types";

const RedEnvelopeRainDialog = lazy(() =>
  import("../components/RedEnvelopeRain/RedEnvelopeRainDialog").then(m => ({
    default: m.RedEnvelopeRainDialog,
  }))
);

const RED_ENVELOPE_RAIN_CONFIG = POPUP_CONFIGS.RED_ENVELOPE_RAIN;
const EMPTY_LIST: RedEnvelopeRainActivityVO[] = [];

/** 边界定时多等一点，确保到点后重算时已越过边界 */
const BOUNDARY_SLACK_MS = 300;
/** 边界定时上限：设备休眠 / 后台挂起会让长定时器漂移，最多 30 分钟兜底重算一次 */
const MAX_BOUNDARY_WAIT_MS = 30 * 60 * 1000;

/**
 * VO 对象 → 收到它时算出的时钟偏移。
 * 首页配置每次请求都是新对象；同一对象在多次渲染间复用同一个偏移，
 * 不会因为「稍后才渲染」把旧的 serverTime 当成现在而算偏。
 */
const voOffsetCache = new WeakMap<object, number>();
function getVoOffset(vo: RedEnvelopeRainActivityVO): number {
  let offset = voOffsetCache.get(vo);
  if (offset === undefined) {
    offset = computeClockOffset(vo.serverTime);
    voOffsetCache.set(vo, offset);
  }
  return offset;
}

/**
 * 弹窗里拉到的 status / 刚领到的场次，**替换**首页 VO 里的已占用数据（首页配置刷新前浮标也能立即切到下一场）。
 * 以最新的服务端数据为准：新 status 到达即整体替换，首页配置刷新（list 变化）即全部丢弃回到 VO；
 * 不取并集 / max——PROCESSING 后被释放成 FAILED 时，下一份服务端数据会把「已领」撤掉。
 */
interface ClaimOverlay {
  date: string;
  keys: string[];
  count: number;
}

/**
 * 弹窗挂载标记：挂载期间 flagRef = true，卸载（关闭 / 被队列移除）即复位。
 * 用组件生命周期而不是在 close 里手动复位——弹窗被队列直接移除时也不会把标记卡死。
 */
const MountFlag: React.FC<{ flagRef: React.MutableRefObject<boolean> }> = ({
  flagRef,
}) => {
  useEffect(() => {
    flagRef.current = true;
    return () => {
      flagRef.current = false;
    };
  }, [flagRef]);
  return null;
};

const toStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/**
 * 红包雨（新）弹窗 hook（结构参照 usePhoneVerifyActivityPopup）
 * - 不自动弹（autoTrigger: false），只由首页主 banner / 活动列表 banner（OPEN_RED_ENVELOPE_RAIN_EVENT）
 *   和浮标点击打开；
 * - 打开前先调 status：show=false → toast 原因不打开（并刷新首页配置让入口消失）；
 * - 浮标阶段 / 倒计时按首页 VO 的绝对毫秒 + serverTime 偏移计算（不依赖手机时区），
 *   到达场次边界自动重算，跨过 dayEndMs 刷新首页配置；
 * - 桶缺失（游客 / 无有效活动 / 后端未上线）时浮标不显示、trigger 无动作。
 */
export const useRedEnvelopeRainPopup = (): RedEnvelopeRainPopupResult => {
  const t = useT();
  const currencySymbol = useCurrencySymbol();
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const rawList = useRedEnvelopeRainList() as RedEnvelopeRainActivityVO[];
  // 桶缺失时 selector 每次返回新的 []：换成稳定的空数组，避免下游 callback / 浮标 memo 每次渲染都失效
  const list = rawList.length > 0 ? rawList : EMPTY_LIST;

  // 最新 list 走 ref：trigger 在 await 之后读，避免闭包旧值
  const listRef = useRef(list);
  listRef.current = list;

  /** 待打开的弹窗数据（trigger 拉到 status 后写入，renderContent 读取） */
  const pendingRef = useRef<{
    activityId: string;
    snapshot: RainStatusSnapshot;
  } | null>(null);
  /** status 请求进行中（防连点重复打开） */
  const openingRef = useRef(false);
  /** 弹窗已打开（再次点击入口不重复入队） */
  const dialogOpenRef = useRef(false);

  /** 到达场次边界时 +1：让 getFloatState 引用变化，驱动浮标 memo 重算倒计时 */
  const [clockVersion, setClockVersion] = useState(0);
  const [overlays, setOverlays] = useState<Record<string, ClaimOverlay>>({});

  const shouldShow = isConfigReady && isAuthenticated && list.length > 0;

  // ---------- 弹窗回传：以服务端数据替换已占用名额 ----------
  const handleStatus = useCallback((status: RedEnvelopeRainStatus) => {
    const activityId = status?.activityId;
    if (!activityId) return;
    const date = status.date || "";
    const keys = (Array.isArray(status.slots) ? status.slots : [])
      .filter(s => s?.claimed === true && typeof s.key === "string")
      .map(s => s.key as string);
    // 整体替换（不与旧叠加合并）
    setOverlays(prev => ({
      ...prev,
      [activityId]: {
        date,
        keys,
        count: normalizeClaimCount(status.claimCount),
      },
    }));
  }, []);

  // 刚领到的场次：在「当前生效的数据」（叠加或 VO）上乐观追加一场，直到下一份 status / 首页配置替换它
  const handleClaimed = useCallback(
    (activityId: string, date: string, slotKey: string, claimCount: number) => {
      if (!activityId || !slotKey) return;
      const vo = listRef.current.find(item => item?.id === activityId);
      setOverlays(prev => {
        const old = prev[activityId];
        const baseKeys =
          old && old.date === date
            ? old.keys
            : vo && (vo.date || "") === date
              ? toStringArray(vo.claimedSlotKeys)
              : [];
        return {
          ...prev,
          [activityId]: {
            date,
            keys: Array.from(new Set([...baseKeys, slotKey])),
            // 弹窗传来的是「当前生效已占用数 + 1」
            count: claimCount,
          },
        };
      });
    },
    []
  );

  const { trigger: originalTrigger } = useQueuedPopup({
    popupId: RED_ENVELOPE_RAIN_CONFIG.id,
    priority: RED_ENVELOPE_RAIN_CONFIG.priority,
    // 只有 trigger 拉到 show=true 的 status 后才允许入队
    shouldShow: () => isAuthenticated && !!pendingRef.current,
    renderContent: onComplete => {
      const pending = pendingRef.current;
      if (!pending) return null;
      const close = () => {
        pendingRef.current = null;
        onComplete();
      };
      return (
        <>
          <MountFlag flagRef={dialogOpenRef} />
          <Suspense fallback={null}>
            <RedEnvelopeRainDialog
              activityId={pending.activityId}
              initialSnapshot={pending.snapshot}
              onClose={close}
              onStatus={handleStatus}
              onClaimed={handleClaimed}
            />
          </Suspense>
        </>
      );
    },
    // 不做自动弹窗（契约 §1-10），只能点入口打开
    allowManualTrigger: true,
    autoTrigger: false,
    // ⚠️共通调用：弹窗队列在未配置 allowedPaths / blockedPaths 时默认只允许首页展示；
    // 2026-10-14 新增红包雨活动页（进页面自动弹窗、底部按钮打开弹窗），需显式放行首页 + 活动页
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, ROUTES.RED_ENVELOPE_RAIN],
    dependencies: [isAuthenticated],
    userBinding: true,
    getUserId: () => userInfo?.userId,
  });

  // useQueuedPopup 的 trigger 每次渲染可能是新函数：await 之后走 ref 调最新的
  const originalTriggerRef = useRef(originalTrigger);
  originalTriggerRef.current = originalTrigger;

  const toastReason = useCallback(
    (code?: string | null) => {
      Message.failed(
        getRainReasonText(t, code, null, {
          // 金额按币种小数位向下截断（规则同后端 Currency.amountScare）
          formatAmount: n =>
            formatRainAmount(n, currencySymbol, getRainCurrencyId()),
        })
      );
    },
    [t, currencySymbol]
  );

  const trigger = useCallback(
    (activityId?: string) => {
      if (dialogOpenRef.current || openingRef.current) return;
      const uid = getRainUserId();
      if (!uid) {
        // 游客：后端不下发入口，这里兜底拉登录
        openLoginRegisterPopup({ defaultMode: "login" });
        return;
      }
      const id = activityId || listRef.current[0]?.id;
      if (!id) return;
      openingRef.current = true;
      (async () => {
        try {
          const snap = await fetchRainStatus(id);
          // 请求期间切换了账号：结果属于上一个账号（statusCache 已丢弃），不提示、不打开
          if (getRainUserId() !== uid) return;
          if (!snap) {
            toastReason("UNKNOWN");
            return;
          }
          if (snap.status.show !== true) {
            // show=false：不打开，toast 原因（缺省按活动未开放），刷新首页配置让入口按最新状态显隐
            toastReason(snap.status.reason || "ACTIVITY_INVALID");
            loadConfig().catch(() => {});
            return;
          }
          handleStatus(snap.status);
          // 候选活动被回退锁定到另一个活动（参与条件不满足按 sort 换下一个）：刷新首页配置，
          // 让浮标倒计时跟着实际锁定的活动走
          if (snap.status.activityId && snap.status.activityId !== id) {
            loadConfig().catch(() => {});
          }
          pendingRef.current = { activityId: id, snapshot: snap };
          originalTriggerRef.current();
        } finally {
          openingRef.current = false;
        }
      })();
    },
    [toastReason, handleStatus]
  );

  // 监听首页 banner / useDisplayItemClick 派发的「打开红包雨弹窗」事件
  useEffect(() => {
    const handler = (e: Event) => {
      const activityId = (e as CustomEvent<{ activityId?: string }>).detail
        ?.activityId;
      trigger(activityId);
    };
    window.addEventListener(OPEN_RED_ENVELOPE_RAIN_EVENT, handler);
    return () =>
      window.removeEventListener(OPEN_RED_ENVELOPE_RAIN_EVENT, handler);
  }, [trigger]);

  // 账号变化：清掉上一个账号的叠加数据
  const userId = userInfo?.userId;
  useEffect(() => {
    setOverlays({});
  }, [userId]);

  // 首页配置刷新（list 换成新对象）：VO 已是最新服务端数据，丢弃全部叠加
  useEffect(() => {
    setOverlays({});
  }, [list]);

  const findVo = useCallback(
    (activityId?: string) =>
      activityId ? list.find(item => item?.id === activityId) : list[0],
    [list]
  );

  /** 单个 VO 的阶段计算输入（叠加存在时以叠加替换 VO 的已占用数据；字段缺失一律兜底） */
  const buildPhaseInput = useCallback(
    (vo: RedEnvelopeRainActivityVO) => {
      const date = vo.date || "";
      const ov = overlays[vo.id];
      const useOv = !!ov && ov.date === date;
      const claimed = new Set<string>(
        useOv ? ov.keys : toStringArray(vo.claimedSlotKeys)
      );
      const dayEnd = Number(vo.dayEndMs);
      return {
        slots: normalizeRainSlots(vo.slots),
        nextDaySlots: normalizeRainSlots(vo.nextDaySlots),
        claimedSlotKeys: claimed,
        claimCount: useOv ? ov.count : normalizeClaimCount(vo.claimCount),
        dailyTimes: normalizeDailyTimes(vo.dailyTimes),
        dayEndMs:
          vo.dayEndMs !== null &&
          vo.dayEndMs !== undefined &&
          Number.isFinite(dayEnd)
            ? dayEnd
            : null,
      };
    },
    [overlays]
  );

  // 场次边界定时：到点 +1 clockVersion 让浮标重算；跨过 dayEndMs 刷新首页配置
  useEffect(() => {
    if (list.length === 0) return;
    let minDelay: number | null = null;
    for (const vo of list) {
      if (!vo?.id) continue;
      const offset = getVoOffset(vo);
      const now = serverNow(offset);
      const boundary = getNextRainBoundary(buildPhaseInput(vo), now);
      if (boundary === null) continue;
      const delay = boundary - now + BOUNDARY_SLACK_MS;
      if (minDelay === null || delay < minDelay) minDelay = delay;
    }
    if (minDelay === null) return;
    const timer = setTimeout(
      () => {
        const crossedDay = listRef.current.some(vo => {
          const dayEnd = Number(vo?.dayEndMs);
          return Number.isFinite(dayEnd) && serverNow(getVoOffset(vo)) > dayEnd;
        });
        if (crossedDay) loadConfig().catch(() => {});
        setClockVersion(v => v + 1);
      },
      Math.min(Math.max(0, minDelay), MAX_BOUNDARY_WAIT_MS)
    );
    return () => clearTimeout(timer);
  }, [list, buildPhaseInput, clockVersion]);

  // 从后台切回前台：定时器可能被挂起，立刻重算一次
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        setClockVersion(v => v + 1);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const isActivityAvailable = useCallback(
    (activityId?: string) => !!findVo(activityId),
    [findVo]
  );

  // clockVersion 变化即生成新函数：浮标 memo 依赖本 hook 返回值，借此在场次边界重算倒计时
  const getFloatState = useCallback(
    (activityId?: string): RedEnvelopeRainFloatState | null => {
      const vo = findVo(activityId);
      if (!vo) return null;
      const now = serverNow(getVoOffset(vo));
      const phase = computeRainPhase(buildPhaseInput(vo), now);
      if (phase.phase === "ENDED" || phase.targetMs === null) {
        return { phase: "ENDED", remainingMs: 0, targetMs: null };
      }
      return {
        phase: phase.phase,
        remainingMs: Math.max(0, phase.targetMs - now),
        targetMs: phase.targetMs,
      };
    },
    // clockVersion 是刻意的重算触发器（见上），函数体内不直接读取
    [findVo, buildPhaseInput, clockVersion]
  );

  const getFloatIconUrl = useCallback(
    (activityId?: string) =>
      findVo(activityId)?.entryPoint?.floatingIconImageUrl || "",
    [findVo]
  );

  /** 活动配置的右下浮标落位；没配返回 undefined（由调用方回落到展示项 floatSlot） */
  const getFloatSlot = useCallback(
    (activityId?: string): "fixed" | "swiper" | undefined => {
      const slot = findVo(activityId)?.entryPoint?.floatingIconSlot;
      if (slot === "fixed") return "fixed";
      if (slot === "swiper") return "swiper";
      return undefined;
    },
    [findVo]
  );

  return useMemo(
    () => ({
      shouldShow,
      trigger,
      isActivityAvailable,
      getFloatState,
      getFloatIconUrl,
      getFloatSlot,
    }),
    [
      shouldShow,
      trigger,
      isActivityAvailable,
      getFloatState,
      getFloatIconUrl,
      getFloatSlot,
    ]
  );
};
